import { Link, Navigate, useSearchParams } from 'react-router-dom';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';
import { seedVarietyProductDisplayTitle } from './seedVarietyCatalog';
import { useSeedVarietyProducts } from './useSeedVarietyProducts';

export default function SeedVarietyIndex() {
  const { site, products, ready } = useSeedVarietyProducts();
  const [searchParams] = useSearchParams();
  const categoryId = (searchParams.get('category') || '').trim();

  if (!ready) {
    return (
      <div className="min-h-screen bg-brand-cream/40 text-neutral-900">
        <MarketingSiteHeader
          site={site}
          idPrefix="seed-varieties"
          alwaysSolid
          isNavLinkCurrent={marketingHeaderNavIsCurrent}
        />
        <div className="flex min-h-[50vh] items-center justify-center text-neutral-500 pt-[calc(4.5rem+env(safe-area-inset-top))]">
          Loading…
        </div>
      </div>
    );
  }

  if (categoryId) {
    return <Navigate to={`/crops/${encodeURIComponent(categoryId)}`} replace />;
  }

  return (
    <div className="min-h-screen bg-brand-cream/40 text-neutral-900">
      <MarketingSiteHeader
        site={site}
        idPrefix="seed-varieties"
        alwaysSolid
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <main className="mx-auto max-w-shell px-4 py-12 pt-[calc(4.25rem+env(safe-area-inset-top))] sm:px-6 sm:py-12 sm:pt-[calc(4.75rem+env(safe-area-inset-top))] lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-olive">Variety specifications</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
          Technical product sheets
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-600">
          Detailed specs by variety (breeder, maturity, yield, and more). This section is separate from the main
          product catalog above the fold.
        </p>

        {products.length === 0 ? (
          <p className="mt-10 text-sm text-neutral-600">No variety products published yet.</p>
        ) : (
          <ul className="mt-10 grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:gap-8">
            {products.map((product) => {
              const first = product.variants[0];
              const cardTitle = seedVarietyProductDisplayTitle(product);
              return (
                <li key={product.id}>
                  <Link
                    to={`/seed-varieties/${product.id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white no-underline shadow-sm transition-shadow hover:shadow-md"
                  >
                    {first ? (
                      <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                        <img
                          src={first.image}
                          alt={first.alt || cardTitle}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        />
                      </div>
                    ) : null}
                    <div className="flex flex-1 flex-col px-5 pb-5 pt-4 sm:px-6 sm:pt-5">
                      {product.familyLabel && product.categoryName && product.familyLabel !== product.categoryName ? (
                        <p className="text-xs font-semibold uppercase tracking-wide text-brand-olive">
                          {product.categoryName}
                        </p>
                      ) : null}
                      <h2 className="text-xl font-bold text-neutral-900">{cardTitle}</h2>
                      {product.summary ? (
                        <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-600">{product.summary}</p>
                      ) : null}
                      <p className="mt-4 text-sm font-semibold text-brand-olive">
                        {product.variants.length} variant{product.variants.length === 1 ? '' : 's'} — view specs
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
